<template>
  <form class="share" @submit.prevent="submit()">
    <span class="share__title">{{popupTitle}}</span>
    <label class="share__form-group">
      <span class="share__label">To:</span>
      <input type="text"
             ref="email"
             name="email"
             class="form-control"
             :class="{'share__form-group_invalid': errors.has('email')}"
             placeholder="Enter email address"
             v-validate="getEmailValidator"
             v-model="email">
      <span class="error__message">{{ errors.first('email') }}</span>
    </label>
    <label class="share__form-group">
      <span class="share__label">Subject:</span>
      <input type="text"
             name="subject"
             class="form-control"
             :class="{'share__form-group_invalid': errors.has('subject')}"
             placeholder="Subject"
             v-validate="'required'"
             v-model="subject">
      <span class="error__message">{{ errors.first('subject') }}</span>
    </label>
    <label class="share__form-group">
      <span class="share__label">Description:</span>
      <div class="form-control description__wrapper"
           :class="{'description_invalid': errors.has('description')}">
      <textarea
        class="description"
        name="description"
        v-validate="getTextAreaValidator"
        rows="6"
        placeholder="Enter description"
        v-model="description"></textarea>
        <span class="error__message-description">{{ errors.first('description') }}</span>
      </div>
    </label>
    <label class="share__form-group">
      <span class="share__label">{{lastFieldLabel}}</span>
      <div class="share__link-wrapper">
          <base-material-design-icon
              class="share__link-icon-file"
              icon="filePdf"
              v-if="!isInvitePopup"/>
        <input type="text"
               class="form-control share__link-field"
               :class="{'share__link_shifted': !isInvitePopup}"
               placeholder="Body"
               disabled
               readonly
               v-model="link">
        <div class="share__link-button"
             @click="copyToClipboard()"  v-if="isInvitePopup">
          <base-material-design-icon icon="copy"/>
        </div>
      </div>
    </label>
    <div class="share__controls">
      <button class="share__button share__button_cancel"
              @click.prevent="cancel()">Cancel
      </button>
      <button class="share__button share__button_submit"
              type="submit">
        Share
      </button>
    </div>
  </form>
</template>

<script>
import ToasterService from '../../../services/toasterService'
import { mapState } from 'vuex'
import moment from 'moment'
import BaseModalMixins from '@/common/BaseModalMixins'

/**
 * Share pdf and share content brief modal
 * For validation the form we use there "vee-validate" library
 */
export default {
  name: 'Share',
  mixins: [BaseModalMixins],
  props: {
    link: {
      type: String,
      required: true
    },
    title: {
      type: String,
      required: true
    },
    userEmail: {
      type: String,
      required: true
    },
    popupType: {
      type: String,
      default: 'invite'
    }
  },
  data () {
    return {
      email: '',
      subject: '',
      description: ''
    }
  },
  beforeMount () {
    this.subject = this.isInvitePopup ? `${this.userEmail} Shared '${this.title}'` : this.title
  },
  mounted () {
    this.$refs.email.focus()
  },
  computed: {
    ...mapState('briefContent', [
      'briefContent'
    ]),
    isFormInvalid () {
      return this.errors.any() ||
          (this.fields.email && this.fields.email.pristine)
    },
    popupTitle () {
      return this.isInvitePopup ? 'Invite' : 'Email me a PDF'
    },
    isInvitePopup () {
      return this.popupType === 'invite'
    },
    getEmailValidator () {
      return this.isInvitePopup ? 'required|email' : 'required|emails'
    },
    getTextAreaValidator () {
      return this.isInvitePopup ? '' : ''
    },
    lastFieldLabel () {
      return this.isInvitePopup ? 'Shared link:' : 'Shared file:'
    },
    tabName () {
      if (this.$route.name === 'BriefStrategy') {
        return 'Strategy'
      }
      if (this.$route.name === 'BriefReporting') {
        return 'Reporting'
      }
      return ''
    }
  },
  methods: {
    submit: async function () {
      if (this.isFormInvalid) {
        // mark as dirty to force validating error be shown
        this.fields.email.dirty = true
        this.fields.description.dirty = true
        this.$validator.validateAll()
        return
      }
      if (this.popupType === 'invite') {
        const dataToShare = {
          to: this.email,
          subject: this.subject,
          body: (`${this.description} ${this.link}`).trim()
        }
        this.$emit('submit', dataToShare)
      } else {
        const name = `${this.selectedClient.name}_ContentBrief_${this.briefContent.name}_${this.tabName}_${moment().format('YYYY-MM-DD')}`.replace(' ', '_')
        let tab = this.$route.path.split('/')
        tab = tab[tab.length - 1]
        const pdfData = {
          to: this.email,
          subject: this.subject,
          body: (`${this.description}`).trim(),
          name,
          tab
        }
        this.$emit('submit', pdfData)
      }
    },
    cancel () {
      this.$emit('cancel')
    },
    copyToClipboard () {
      this.$copyText(this.link)
        .then(
          e => {
            ToasterService.showToast(ToasterService.INFO_TYPE, 'Share link copied to clipboard')
          },
          err => {
            ToasterService.showToast(ToasterService.ERROR_TYPE, 'Can not copy link. Please, copy it manually')
          }
        )
    }
  }
}
</script>

<style scoped lang="scss">
  @import "../../../assets/variables";

  .share {
    display: flex;
    flex-direction: column;
    align-items: stretch;
    padding: 30px;

    &__title {
      text-align: center;
      font-size: 34px;
      font-weight: 500;
      margin: 9px 0 35px;
      color: $rfx-dark-march;
    }

    &__form-group {
      display: flex;
      flex-direction: column;
      position: relative;

      &_invalid[type='text'] {
        border: 1px solid $rfx-red;
        border-radius: 4px;
      }
    }

    &__label {
      display: block;
      font-weight: 600;
      margin-bottom: 5px;
      color: $rfx-dark-march;
    }

    &__controls {
      display: flex;
      justify-content: flex-end;
    }

    &__link {
      &-wrapper {
        position: relative;
      }

      &-field[type='text'] {
        width: 100%;
        box-sizing: border-box;
        padding-right: 60px;
        @include ellipsis;
      }

      &_shifted[type='text'] {
        padding-left: 50px;
      }

      &-icon-file {
        color: $rfx-dark-march;
        position: absolute;
        top: 17px;
        left: 22px;
        width: 14px;
        height: 18px;
      }

      &_shifted[type='text'] {
        padding-left: 50px;
      }

      &-button {
        position: absolute;
        z-index: 1;
        width: 50px;
        height: 50px;
        top: 0;
        right: 0;
        display: flex;
        align-items: center;
        justify-content: center;
        color: $rfx-dark-march;
        cursor: pointer;

        &:hover {
          color: $rfx-bright-turquoise;
        }
      }
    }

    &__button {
      width: 100px;
      height: 40px;
      border-radius: 4px;
      font-size: 10px;
      font-weight: 900;
      text-transform: uppercase;
      margin-left: 30px;
      border: none;
      outline: none;
      cursor: pointer;

      &_submit {
        background-color: $rfx-medium-march;
        color: #fff;

        &:disabled {
          pointer-events: none;
          background-color: #fff;
          color: $rfx-neutral-grey;
          cursor: default;
        }
      }

      &_disabled {
        pointer-events: none;
        background-color: #fff;
        color: $rfx-neutral-grey;
        cursor: default;
      }

      &_cancel {
        color: $rfx-dark-march;
        background-color: #fff;
      }
    }
  }

  .form-control {
    height: 50px;
    padding: 0 20px;
    border: 2px solid $rfx-light-steel;
    outline: none;
    border-radius: 4px;
    font-size: 14px;
    font-weight: 600;
    margin-bottom: 30px;
    color: $rfx-dark-march;

    &::placeholder {
      color: $rfx-pearl;
      font-weight: normal;
      font-size: 14px;
    }

    &:disabled {
      background-color: $rfx-light-steel;
    }
  }

  .description {
    width: 100%;
    padding: 0;
    resize: none;
    outline: none;
    border: none;
    font-size: 14px;
    font-weight: 600;

    &::placeholder {
      color: $rfx-medium-grey;
      font-weight: normal;
      font-size: 14px;
    }

    &_invalid {
      border: 1px solid $rfx-red;
      border-radius: 4px;
    }

    &::placeholder {
      color: $rfx-pearl;
      font-weight: normal;
      font-size: 14px;
    }

    &__wrapper {
      padding: 10px 20px;
      height: auto;
    }
  }

  .error {
    &__message-description,
    &__message {
      color: $rfx-red;
      position: absolute;
      top: 75px;
      font-size: 10px;
      font-weight: normal;
    }

    &__message-description {
      top: 154px;
      left: 0;
    }
  }
</style>
